/**
 * Client for the tenant-neutral Legal.* curation API (LEGAL-4,
 * "Betreiberverantwortung" admin panel) — /administration/legal/* on
 * tri-server, admin access only.
 *
 * Keys (obligationKey like 'küo/§2/1', eliId like 'de/bund/kueo/2009-06-16')
 * contain slashes and are therefore always passed as query parameters.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type LegalReviewAction = "confirm" | "needsRevision" | "reject";

export interface LegalObligationFilter {
  reviewStatus?: string; // comma-separated list
  regulationId?: string;
  provisionId?: string;
  regulatedObjectCode?: string;
  provenanceType?: string;
  type?: string;
  minConfidence?: number;
  maxConfidence?: number;
  tier?: number;
  includeSuperseded?: boolean;
  /** Gap queue (SPEC_Hazard_UI §5): obligations with no hazard attached. */
  withoutHazard?: boolean;
  sortBy?: "confidence" | "validFrom" | "recordedAt" | "regulationId" | "obligationKey";
  sortDir?: "asc" | "desc";
  skip?: number;
  limit?: number;
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

export const useLegalAdministrationAPI = () => {
  const baseUrl = `${configuration.serverUrl}/administration/legal`;

  const get = async (path: string, params?: Record<string, any>) => {
    const query = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => [key, String(value)]),
        ).toString()}`
      : "";
    const response = await authenticatedFetch(`${baseUrl}${path}${query}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  const send = async (method: "POST" | "PUT", path: string, body: any) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  return {
    getSummary: () => get("/summary"),
    getVocabulary: () => get("/vocabulary"),

    // Regulation browser
    getRegulations: () => get("/regulations"),
    getProvisions: (regulationId: string) => get("/provisions", { regulationId }),

    // Obligations / review queue
    getObligations: (filter: LegalObligationFilter = {}) => get("/obligations", filter),
    getObligationVersions: (obligationKey: string) =>
      get("/obligation-versions", { key: obligationKey }),
    reviewObligation: (review: {
      obligationKey: string;
      action: LegalReviewAction;
      changes?: Record<string, any>;
      reviewNote?: string;
    }) => send("POST", "/obligations/review", review),
    batchConfirmObligations: (obligationKeys: string[]) =>
      send("POST", "/obligations/batch-confirm", { obligationKeys }),
    getDiffReviews: () => get("/diff-reviews"),

    // Vocabulary & crosswalk
    getRegulatedObjects: () => get("/regulated-objects"),
    saveRegulatedObject: (regulatedObject: any) =>
      send("PUT", "/regulated-objects", regulatedObject),
    getCorrespondences: () => get("/correspondences"),
    saveCorrespondence: (correspondence: any) => send("PUT", "/correspondences", correspondence),
    getTaxonomyBridge: () => get("/taxonomy-bridge"),

    // Derived layer
    getServiceProfiles: () => get("/service-profiles"),
    getChecklistDefinitions: () => get("/checklist-definitions"),
    getMasterChecklists: (obligationKey?: string) =>
      get("/master-checklists", obligationKey ? { key: obligationKey } : undefined),
    reviewMasterChecklist: (review: { masterId: string; action: LegalReviewAction; reviewNote?: string }) =>
      send("POST", "/master-checklists/review", review),
    deriveMasterChecklist: (obligationKey: string, version: number, force = false) =>
      send(
        "POST",
        `/obligations/derive-checklist?key=${encodeURIComponent(obligationKey)}&version=${version}${force ? "&force=true" : ""}`,
        {},
      ),

    // Coverage & deltas
    getCoverage: () => get("/coverage"),
    getDeltas: (params: { regulationId?: string; provisionId?: string; skip?: number; limit?: number } = {}) =>
      get("/deltas", params),

    // Hazard catalogue (SPEC_Hazard_UI §4)
    getHazards: () => get("/hazards"),
    getHazardDetail: (code: string) => get("/hazards/detail", { code }),
    deprecateHazard: (code: string, deprecated: boolean) =>
      send("POST", "/hazards/deprecate", { code, deprecated }),

    // Regulated types and their hazard sets (§2)
    getHazardTypes: () => get("/hazard-types"),
    getHazardTypeDetail: (code: string) => get("/hazard-types/detail", { code }),
    /** Submits the whole set — what gets released is the set, not a single row. */
    saveHazardAssignments: (payload: {
      code: string;
      members: { hazardCode: string; relation: "carries" | "protects"; justification?: string }[];
      changeReason?: string;
    }) => send("POST", "/hazard-types/assignments", payload),

    // Releases (§3)
    getHazardReviewQueue: () => get("/hazard-reviews/queue"),
    prepareHazardReview: (code: string) => get("/hazard-reviews/prepare", { code }),
    submitHazardReview: (payload: {
      code: string;
      mode: "full" | "delta";
      decision: "confirmed" | "rejected" | "partial";
      perItemDecisions?: any[];
      delta?: any[];
      unchangedMembers?: any[];
      fromSetVersion?: number | null;
      trigger?: string;
      note?: string;
      signedBy?: string;
      qualification?: string;
    }) => send("POST", "/hazard-reviews", payload),

    // Severity (§2.3, §3.3)
    overrideHazardSeverity: (payload: {
      regulatedObjectCode: string;
      hazardCode: string;
      siBase: number;
      overrideReason: string;
    }) => send("POST", "/hazard-severities/override", payload),
    confirmHazardSeverities: (items: { regulatedObjectCode: string; hazardCode: string }[]) =>
      send("POST", "/hazard-severities/confirm", { items }),

    // Context part (SPEC_Kontext_Portal §2–§4)
    getContextRules: () => get("/context-rules"),
    saveContextRule: (rule: any) => send("POST", "/context-rules", rule),
    signoffContextRules: (payload: { signedBy?: string; qualification?: string; note?: string }) =>
      send("POST", "/context-rules/signoff", payload),
    getSeverityBands: () => get("/severity-bands"),
    getSimulatorOptions: () => get("/context-simulate/options"),
    /** Side-effect free — pure enquiry, writes no assessment. */
    simulateSeverity: (payload: {
      regulatedObjectCode: string;
      hazardCode: string;
      usageClass?: string | null;
      personExposure?: string | null;
    }) => send("POST", "/context-simulate", payload),
    getContextSweep: () => get("/context-simulate/sweep"),
    /** The whole context space for one (type, hazard) — building use × person presence. */
    getContextMatrix: (regulatedObjectCode: string, hazardCode: string) =>
      get("/context-simulate/matrix", { regulatedObjectCode, hazardCode }),

    // Obligation gap queue (§5)
    getObligationsWithoutHazard: (limit?: number) =>
      get("/obligations/without-hazard", limit ? { limit } : undefined),
    saveObligationMitigatesHazard: (obligationKey: string, hazardCodes: string[]) =>
      send("PUT", "/obligations/mitigates-hazard", { obligationKey, hazardCodes }),
  };
};
